<template>
  <v-card outlined elevation="0" class="quiz-question-card">
    <!-- Header (always visible) -->
    <div
      class="quiz-question-card__header d-flex align-center justify-space-between pa-3 cursor-pointer"
      @click="toggleExpanded"
    >
      <div class="d-flex align-center quiz-question-card__header-left">
        <Icon
          icon="mdi-drag"
          class="question-handle cursor-grab mr-2 flex-shrink-0"
          color="darkGrey"
          @click.stop
        />
        <Typography variant="subtitle-1" class="flex-shrink-0">
          Question {{ questionIndex + 1 }}
        </Typography>
        <Typography
          v-if="isCollapsed && question.content"
          variant="body-1"
          class="quiz-question-card__preview ml-2"
        >
          {{ question.content }}
        </Typography>
      </div>

      <div class="d-flex align-center">
        <Icon :icon="isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up'" />
      </div>
    </div>

    <!-- Body (expanded) -->
    <v-form ref="form" v-show="!isCollapsed" class="quiz-question-card__body pa-4">
      <v-row align="center" no-gutters>
        <v-col cols="10" sm="6">
          <v-select
            :value="question.type"
            outlined
            dense
            label="Question Type*"
            :items="questionTypes"
            item-text="label"
            item-value="value"
            color="brandPrimary"
            hide-details
            :menu-props="{ offsetY: true }"
            @change="handleTypeChange"
          />
        </v-col>
        <v-col cols="2" sm="6" class="d-flex justify-end">
          <Button icon small @click.stop="$emit('delete')">
            <Icon icon="mdi-delete" color="darkGrey" />
          </Button>
        </v-col>
      </v-row>

      <!-- Required toggle -->
      <v-switch
        v-if="showRequiredToggle"
        :input-value="question.required"
        color="brandPrimary"
        label="Make question required"
        hide-details
        class="my-0 py-0"
        @change="updateField('required', $event)"
      />

      <!-- Question content -->
      <v-textarea
        :value="question.content"
        outlined
        rows="2"
        label="Question Content*"
        color="brandPrimary"
        hide-details="auto"
        :rules="[
          (v) => !!v || 'A question is required',
          (v) => !v || v.length <= 750 || 'Question must be 750 characters or fewer',
        ]"
        @input="updateField('content', $event)"
      />

      <!-- MULTIPLE_CHOICE / SELECT_ALL answers -->
      <div v-if="hasAnswerList">
        <Typography v-if="showCorrectAnswers" variant="body-2" as="p">
          <template v-if="question.type === 'MULTIPLE_CHOICE'"> Check a correct answer </template>
          <template v-else> Check one or more correct answers </template>
          (optional)
        </Typography>

        <draggable
          :list="question.LearningQuizAnswers"
          :group="`answers-${questionIndex}`"
          handle=".answer-handle"
          @change="emitQuestionUpdate"
        >
          <QuizAnswerRow
            v-for="(answer, answerIndex) in question.LearningQuizAnswers"
            :key="`answer-${answerIndex}`"
            class="pb-4"
            :answer="answer"
            :show-correctness="showCorrectAnswers"
            :is-required="question.type === 'SELECT_ALL' ? answerIndex < 2 : answerIndex === 0"
            :show-delete-button="
              question.type === 'SELECT_ALL'
                ? question.LearningQuizAnswers.length > 2
                : question.LearningQuizAnswers.length > 1
            "
            @update:content="handleAnswerContentUpdate(answerIndex, $event)"
            @update:correctness="handleCorrectnessUpdate(answerIndex, $event)"
            @delete="removeAnswer(answerIndex)"
          />
        </draggable>

        <Button text color="brandPrimary" icon-left="mdi-plus" class="mt-1" @click="addAnswer">
          Add answer
        </Button>
      </div>

      <!-- SCALE bounds -->
      <div v-else-if="hasScaleBounds">
        <div class="d-flex align-center">
          <v-text-field
            :value="question.lowerBound"
            outlined
            dense
            type="number"
            label="Lower Bound*"
            color="brandPrimary"
            hide-details="auto"
            :rules="lowerBoundRules"
            @input="updateField('lowerBound', $event)"
          />
          <span class="mx-3">&mdash;</span>
          <v-text-field
            :value="question.upperBound"
            outlined
            dense
            type="number"
            label="Upper Bound*"
            color="brandPrimary"
            hide-details="auto"
            :rules="upperBoundRules"
            @input="updateField('upperBound', $event)"
          />
        </div>

        <v-text-field
          v-if="showCorrectAnswers && question.LearningQuizAnswers.length === 1"
          :value="question.LearningQuizAnswers[0].content"
          outlined
          dense
          label="Correct Answer (optional)"
          type="number"
          color="brandPrimary"
          hide-details="auto"
          class="pb-4"
          :rules="correctScaleRules"
          @input="handleScaleCorrectAnswerUpdate($event)"
        />
      </div>
    </v-form>
  </v-card>
</template>

<script>
import draggable from "vuedraggable";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import QuizAnswerRow from "./QuizAnswerRow.vue";
import {
  QUESTION_TYPES,
  MAX_INT_SIZE,
  SCALE_MAX_RANGE,
  createAnswer,
  hasAnswerList as _hasAnswerList,
  hasScaleBounds as _hasScaleBounds,
  resetAnswersForType,
} from "../quizQuestionTypes";

export default {
  name: "QuizQuestionCard",
  components: {
    draggable,
    Typography,
    Button,
    Icon,
    QuizAnswerRow,
  },
  props: {
    question: { type: Object, required: true },
    questionIndex: { type: Number, required: true },
    collapsed: { type: Boolean, default: null },
    showRequiredToggle: { type: Boolean, default: false },
    showCorrectAnswers: { type: Boolean, default: false },
  },
  emits: ["update:question", "delete", "deleted-answers", "expand-toggle"],
  data() {
    return {
      internalCollapsed: false,
      questionTypes: QUESTION_TYPES,
    };
  },
  computed: {
    isCollapsed() {
      return this.collapsed !== null ? this.collapsed : this.internalCollapsed;
    },
    hasAnswerList() {
      return _hasAnswerList(this.question.type);
    },
    hasScaleBounds() {
      return _hasScaleBounds(this.question.type);
    },
    lowerBoundRules() {
      return [
        (v) => (v !== undefined && v !== null && v !== "") || "A lower bound is required",
        (v) => !v || !v.toString().includes(".") || "Must be a whole number",
        (v) =>
          !v ||
          this.question.upperBound == null ||
          parseInt(v) < parseInt(this.question.upperBound) ||
          "Must be less than the upper bound",
        (v) =>
          !v ||
          this.question.upperBound == null ||
          parseInt(this.question.upperBound) - parseInt(v) <= SCALE_MAX_RANGE ||
          "Range must be 1,000 or less",
        (v) => !v || parseInt(v) < MAX_INT_SIZE || `Must be less than ${MAX_INT_SIZE}`,
      ];
    },
    upperBoundRules() {
      return [
        (v) => (v !== undefined && v !== null && v !== "") || "An upper bound is required",
        (v) => !v || !v.toString().includes(".") || "Must be a whole number",
        (v) =>
          !v ||
          parseInt(v) > parseInt(this.question.lowerBound) ||
          "Must be greater than the lower bound",
        (v) =>
          !v ||
          this.question.lowerBound == null ||
          parseInt(v) - parseInt(this.question.lowerBound) <= SCALE_MAX_RANGE ||
          "Range must be 1,000 or less",
        (v) => !v || parseInt(v) < MAX_INT_SIZE || `Must be less than ${MAX_INT_SIZE}`,
      ];
    },
    correctScaleRules() {
      return [
        (v) => !v || !v.toString().includes(".") || "Must be a whole number",
        (v) =>
          !v ||
          parseInt(v) >= parseInt(this.question.lowerBound) ||
          "Must be greater than or equal to the lower bound",
        (v) =>
          !v ||
          parseInt(v) <= parseInt(this.question.upperBound) ||
          "Must be less than or equal to the upper bound",
      ];
    },
  },
  methods: {
    toggleExpanded() {
      if (this.collapsed !== null) {
        this.$emit("expand-toggle");
      } else {
        this.internalCollapsed = !this.internalCollapsed;
      }
    },
    validate() {
      const valid = this.$refs.form.validate();
      if (!valid && this.isCollapsed) {
        if (this.collapsed !== null) {
          this.$emit("expand-toggle");
        } else {
          this.internalCollapsed = false;
        }
      }
      return valid;
    },
    updateField(field, value) {
      this.$emit("update:question", { ...this.question, [field]: value });
    },
    emitQuestionUpdate() {
      this.$emit("update:question", { ...this.question });
    },
    handleTypeChange(newType) {
      const updated = { ...this.question, type: newType };
      const deletedIds = resetAnswersForType(updated);
      if (deletedIds.length) {
        this.$emit("deleted-answers", deletedIds);
      }
      this.$emit("update:question", updated);
    },
    handleAnswerContentUpdate(answerIndex, value) {
      const answers = [...this.question.LearningQuizAnswers];
      answers[answerIndex] = { ...answers[answerIndex], content: value };
      this.$emit("update:question", { ...this.question, LearningQuizAnswers: answers });
    },
    handleCorrectnessUpdate(answerIndex, value) {
      const answers = [...this.question.LearningQuizAnswers];
      answers[answerIndex] = { ...answers[answerIndex], correctness: value };

      if (value === 1 && this.question.type === "MULTIPLE_CHOICE") {
        answers.forEach((a, i) => {
          if (i !== answerIndex) answers[i] = { ...a, correctness: 0 };
        });
      }

      this.$emit("update:question", { ...this.question, LearningQuizAnswers: answers });
    },
    handleScaleCorrectAnswerUpdate(value) {
      const answers = [...this.question.LearningQuizAnswers];
      answers[0] = { ...answers[0], content: value };
      this.$emit("update:question", { ...this.question, LearningQuizAnswers: answers });
    },
    addAnswer() {
      const answers = [...this.question.LearningQuizAnswers, createAnswer()];
      this.$emit("update:question", { ...this.question, LearningQuizAnswers: answers });
    },
    removeAnswer(answerIndex) {
      const answer = this.question.LearningQuizAnswers[answerIndex];
      if (answer.learningQuizAnswerId) {
        this.$emit("deleted-answers", [answer.learningQuizAnswerId]);
      }
      const answers = this.question.LearningQuizAnswers.filter((_, i) => i !== answerIndex);
      this.$emit("update:question", { ...this.question, LearningQuizAnswers: answers });
    },
  },
};
</script>

<style scoped>
.quiz-question-card__body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.quiz-question-card__header-left {
  min-width: 0;
  overflow: hidden;
}

.quiz-question-card__preview {
  font-weight: normal;
  color: rgba(0, 0, 0, 0.6);
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 0;
}
</style>
